import React, { useState, useEffect } from 'react';
import { Table, Space, Button, Input, Card, Modal, message, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { employeeService } from '../../../services/employee/employee.service';
import { Employee, PaginatedResponse } from '../../../types';
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler';
import { ExclamationCircleOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { confirm } = Modal;
const { Search } = Input;

const Employees: React.FC = () => {
    const { token, selectedBranch } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState<PaginatedResponse<Employee> | null>(null);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { handleError } = useApiErrorHandler();

    const fetchEmployees = async () => {
        if (!token || !selectedBranch) return;
        
        setLoading(true);
        try {
            const response = await employeeService.listEmployees(token, {
                page: currentPage,
                per_page: pageSize,
                search: search
            }, selectedBranch.id);
            setEmployees(response);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, [currentPage, pageSize, search]);

    const handleDelete = async (id: number) => {
        if (!token || !selectedBranch) return;

        confirm({
            title: 'Are you sure you want to delete this employee?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                setDeleteLoading(true);
                try {
                    await employeeService.deleteEmployee(id, token, selectedBranch.id);
                    message.success('Employee deleted successfully');
                    fetchEmployees();
                } catch (error) {
                    handleError(error);
                } finally {
                    setDeleteLoading(false);
                }
            }
        });
    };

    const columns = [
        {
            title: 'Employee Code',
            dataIndex: 'employee_code',
            key: 'employee_code',
            sorter: true,
        },
        {
            title: 'Full Name',
            dataIndex: 'full_name',
            key: 'full_name',
            sorter: true,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
        },
        {
            title: 'Job Title',
            dataIndex: 'job_title',
            key: 'job_title',
            sorter: true,
        },
        {
            title: 'Employment Type',
            dataIndex: 'employment_type',
            key: 'employment_type',
            sorter: true,
        },
        {
            title: 'Status',
            dataIndex: 'employment_status',
            key: 'employment_status',
            sorter: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Employee) => (
                <Space size="small">
                    <Tooltip title="View">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/employees/${record.id}`)} />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/employees/${record.id}/edit`)} />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.id)}
                            loading={deleteLoading}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title="Employees"
            extra={
                <Space>
                    <Search
                        placeholder="Search employees..."
                        onSearch={value => {
                            setSearch(value);
                            setCurrentPage(1);
                        }}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <Button type="primary" onClick={() => navigate('/employees/create')}>
                        Add Employee
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={employees?.data || []}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: employees?.total || 0,
                    onChange: (page, pageSize) => {
                        setCurrentPage(page);
                        setPageSize(pageSize);
                    },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />
        </Card>
    );
};

export default Employees;
